import { buildStructuredOverlayIndex } from '../simulator/structured-state.js?v=20260817-relbridge9';
import { classifyPhysicalLayout } from '../simulator/physical-layout.js?v=20260816-physical2';

const FIELD_INDEX_CACHE = new WeakMap();

function normalizedFieldIndex(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const cached = FIELD_INDEX_CACHE.get(obj);
  if (cached) return cached;
  const exact = new Map();
  const entries = [];
  for (const [key, value] of Object.entries(obj)) {
    const normalizedKey = String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
    exact.set(normalizedKey, value);
    entries.push([normalizedKey, value]);
  }
  const index = { exact, entries };
  FIELD_INDEX_CACHE.set(obj, index);
  return index;
}

function findVal(obj, fields) {
  const index = normalizedFieldIndex(obj);
  if (!index) return '';
  for (const f of fields) {
    const cleanF = String(f).toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [cleanK, value] of index.entries) {
      if (cleanK === cleanF || cleanK.includes(cleanF)) return String(value);
    }
  }
  return '';
}
function num(raw, fallback=0) { const m=String(raw??'').match(/([0-9]+(?:\.[0-9]+)?)/); const n=m?Number(m[1]):NaN; return Number.isFinite(n)?n:fallback; }


function exactVal(obj, fields) {
  const index = normalizedFieldIndex(obj);
  if (!index) return '';
  for (const field of fields) {
    const key = String(field).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (index.exact.has(key)) return index.exact.get(key);
  }
  return '';
}

export function normalizeGpuRecord(g) {
  // SFF GPU source schema is Length / Width / Thickness:
  //   Length    = card length along PCIe axis
  //   Width     = total card height above/below the PCIe bracket plane
  //   Thickness = card depth / slot thickness
  // Internal spatial axes retain the established l/w/h contract where w is thickness
  // and h is GPU height, so map the source fields explicitly instead of guessing aliases.
  const l = num(exactVal(g, ['length','lengthmm','gpulength','gpulengthmm','len']), 0);
  const h = num(exactVal(g, ['width','widthmm','gpuwidth','gpuwidthmm','gpuheight','gpuheightmm','height','heightmm','cardheight','totalheight']), 0);
  const w = num(exactVal(g, ['thickness','thicknessmm','gputhickness','gputhicknessmm','thick']), 0);
  const catalogId=String(exactVal(g,['id','catalogid'])||'').trim();
  return {
    ...g,
    catalogId,
    l, w, h,
    gpuLength: l,
    gpuHeight: h,
    gpuThickness: w,
    gpuHeightKnown: h > 0,
    dimensionKnown: { l:l>0, w:w>0, h:h>0 },
    gpuHeightSource: h > 0 ? 'catalog_width' : 'missing'
  };
}

export function normalizeCoolerRecord(c) {
  const l=num(exactVal(c, ['length','coolerlength','lengthmm','l']), 0);
  const w=num(exactVal(c, ['width','coolerwidth','widthmm','w']), 0);
  const h=num(exactVal(c, ['height','coolerheight','heightmm','h']), 0);
  const rawRamClearance=String(exactVal(c, ['ramclearancemm','ramclearance','memoryclearancemm','memoryclearance']) ?? '').trim();
  const clearanceText=rawRamClearance.toLowerCase();
  const ramClearanceOpen=/(?:no\s*limit|unlimited|open)/.test(clearanceText);
  const ramClearance=num(rawRamClearance,0);
  const ramClearanceKnown=ramClearanceOpen || ramClearance>0;
  const coolerType=String(exactVal(c,['type','coolertype','coolerstyle'])||'').trim();
  const clearanceLabel=ramClearanceOpen ? 'RAM no limit' : (ramClearance>0 ? `RAM \u2264${Math.round(ramClearance*10)/10}mm` : 'RAM clearance ?');
  const catalogId=String(exactVal(c,['id','catalogid'])||'').trim();
  return {
    ...c,
    catalogId,
    l,w,h,
    coolerType,
    ramClearance,
    ramClearanceKnown,
    ramClearanceOpen,
    rawRamClearance,
    dimensionKnown:{l:l>0,w:w>0,h:h>0},
    dimensionSource:'catalog_cooler_dimensions',
    dimensionLabel:`${l}x${w}x${h}mm \u00b7 ${clearanceLabel}`
  };
}

export function normalizeRamRecord(r) {
  const heightFields=[
    'heightinclheatsinkmm','heightinclheatsink','heightincludingheatsinkmm','heightincludingheatsink','heightwithheatsinkmm','heightwithheatsink','ramheight','memoryheight','heightmm','height'
  ];
  const rawHeight=exactVal(r,heightFields) || findVal(r,heightFields);
  const height=num(rawHeight,0);
  const brand=String(exactVal(r,['brand','manufacturer'])||'').trim();
  const model=String(exactVal(r,['model','memory','ram','name'])||'').trim();
  const memoryType=String(exactVal(r,['memorytype','type','ddrtype'])||'').trim().toUpperCase();
  const rgbRaw=String(exactVal(r,['rgb','argb','lighting'])||'').trim();
  const rgb=/^(?:y|yes|1|true|rgb|argb)$/i.test(rgbRaw);
  const catalogId=String(exactVal(r,['id','catalogid'])||'').trim();
  const ramKitModules=2;
  // Only module height is authoritative in the source sheet. Length/depth are deliberately
  // visual UDIMM envelope defaults so they never become new compatibility constraints.
  const visualLength=133;
  const visualDepth=7;
  return {
    ...r,
    catalogId,
    brand,model,memoryType,rgb,rgbRaw,ramKitModules,
    l:visualLength,w:visualDepth,h:height,
    ramHeight:height,
    ramHeightKnown:height>0,
    dimensionKnown:{l:false,w:false,h:height>0},
    dimensionSource:'catalog_ram_height_visual_udimm_envelope',
    dimensionLabel:`${height>0?Math.round(height*100)/100:'?'}mm high${memoryType?` \u00b7 ${memoryType}`:''}${rgb?' \u00b7 RGB':''}`
  };
}

export function normalizeAioRecord(a) {
  // The AIO sheet is authoritative for the physical radiator body. The source headers
  // normalize to names such as `radiatorlengthmm`, `radiatorwidthmm` and
  // `radiatorthicknessmm`; V1.5 omitted those exact aliases and therefore fell through to
  // nominal 240/280/360 geometry for almost every row.
  const declaredSize=num(exactVal(a,['radiatortype','radiatorsize','radiatorsizemm','radsize','size','type']),0);
  const standard=nominalRadiatorGeometry(declaredSize);
  const rawL=exactVal(a,['radiatorlengthmm','radiatorlength','lengthmm','length','l','len']);
  const rawW=exactVal(a,['radiatorwidthmm','radiatorwidth','widthmm','width','w']);
  const rawH=exactVal(a,['radiatorthicknessmm','radiatorthickness','thicknessmm','thickness','thick','t','h']);
  const l=num(rawL,standard?.l||0);
  const w=num(rawW,standard?.w||0);
  const h=num(rawH,standard?.h||0);
  const inferredSize=declaredSize || (l>=430?420:l>=370?360:l>=300?280:l>=250?240:l>=150?140:l>0?120:0);

  const rawFanThickness=exactVal(a,['fanthicknessmm','fanthickness','includedfanthicknessmm','includedfanthickness']);
  const fanThickness=num(rawFanThickness,0);
  const rawFanSize=exactVal(a,['fansizemm','fansize','fanframe','fanframesize']);
  const fanSize=num(rawFanSize,0) || (inferredSize===140 || inferredSize===280 || inferredSize>=420 ? 140 : 120);
  const fanCount=num(exactVal(a,['fans','fancount','numberoffans','includedfans']),0) || Math.max(1,Math.round((inferredSize||fanSize)/Math.max(1,fanSize)));
  const fanProfile=fanThickness>0 && fanThickness<=17 ? 'slim' : 'standard';
  const hasCatalogBody=!!rawL && !!rawW && !!rawH;

  // AIO CPU-side assembly data is independent from the radiator body. Preserve the
  // source dimensions where available; only length/width receive a visual fallback when
  // a real block height exists. Unknown block height never becomes invented clearance.
  const rawBlockL=exactVal(a,['cpublocklengthmm','cpublocklength','blocklengthmm','blocklength','coldplatelengthmm','coldplatelength']);
  const rawBlockW=exactVal(a,['cpublockwidthmm','cpublockwidth','blockwidthmm','blockwidth','coldplatewidthmm','coldplatewidth']);
  const rawBlockH=exactVal(a,['cpublockheightmm','cpublockheight','blockheightmm','blockheight','pumpblockheightmm','pumpblockheight']);
  const blockH=num(rawBlockH,0);
  const blockL=blockH>0 ? num(rawBlockL,70) : 0;
  const blockW=blockH>0 ? num(rawBlockW,70) : 0;
  const rawTubeLength=exactVal(a,['tubelengthmm','tubelength','hoselengthmm','hoselength','tubinglengthmm','tubinglength']);
  const parsedTubeLength=num(rawTubeLength,0);
  const tubeLengthKnown=parsedTubeLength>0;
  // Unknown / `?` tube length remains explicitly unknown, but the visual routing layer needs
  // a finite working budget. 350 mm is a conservative generic AIO fallback, never catalogue truth.
  const tubeLength=tubeLengthKnown?parsedTubeLength:350;
  const pumpLocation=String(exactVal(a,['pumplocation','pumpplacement','pumpposition','pump'])||'').trim();
  const catalogId=String(exactVal(a,['id','catalogid'])||'').trim();
  const aioBlock=blockH>0 ? {
    l:blockL,w:blockW,h:blockH,
    dimensionKnown:{l:!!rawBlockL,w:!!rawBlockW,h:!!rawBlockH},
    dimensionSource:rawBlockL&&rawBlockW&&rawBlockH?'catalog_cpu_block_dimensions':'catalog_block_height_visual_footprint_fallback'
  } : null;

  return {
    ...a,
    catalogId,
    l,w,h,
    radiatorSize:inferredSize,
    fanThickness:fanThickness||25,
    fanThicknessKnown:fanThickness>0,
    fanSize,
    fanCount,
    fanProfile,
    radFan:fanProfile,
    aioBlock,
    tubeLength,
    tubeLengthKnown,
    tubeLengthAssumed:!tubeLengthKnown,
    pumpLocation,
    dimensionKnown:{l:!!rawL,w:!!rawW,h:!!rawH},
    dimensionSource:hasCatalogBody?'catalog_radiator_dimensions':'declared_rad_size_fallback'
  };
}



function normalizeMotherboardFormFactor(raw = '', sourceKey = '', dims = {}) {
  const combined = `${raw || ''} ${sourceKey || ''}`.toUpperCase().replace(/[_]+/g,'-');
  if (/MINI[- ]?DTX|\bDTX\b/.test(combined)) return 'DTX';
  if (/MICRO[- ]?ATX|M[- ]?ATX|MATX/.test(combined)) return 'MATX';
  if (/MINI[- ]?ITX|\bITX\b/.test(combined)) return 'ITX';
  if (/\bATX\b/.test(combined)) return 'ATX';
  const l=Number(dims?.l)||0, w=Number(dims?.w)||0;
  if (l > 203 || w > 180) return 'MATX';
  return 'ITX';
}

function nominalMotherboardOutline(formFactor = 'ITX') {
  const ff=String(formFactor||'ITX').toUpperCase();
  if (ff === 'ATX') return {l:305,w:244};
  if (ff === 'MATX') return {l:244,w:244};
  if (ff === 'DTX') return {l:203,w:170};
  return {l:170,w:170};
}

export function normalizeMotherboardRecord(m, sourceKey = '') {
  if (!m || typeof m !== 'object') return null;
  const brand=String(exactVal(m,['brand','mfr','manufacturer','vendor'])||'').trim();
  const model=String(exactVal(m,['name','model','board','motherboard','title'])||'').trim();
  if (!model) return null;
  const socket=String(exactVal(m,['socket'])||'').trim();
  const chipset=String(exactVal(m,['chipset'])||'').trim();
  const catalogId=String(exactVal(m,['id','catalogid'])||'').trim();
  const rawRamSlots=num(exactVal(m,['ramslots','memoryslots','dimmslots','ramslotcount']),0);
  const ramType=String(exactVal(m,['ramtype','memorytype','ddrtype'])||'').trim().toUpperCase();
  const rawHeight=num(exactVal(m,['sizeheightmm','sizeheight','heightmm','height','lengthmm','length']),0);
  const rawWidth=num(exactVal(m,['sizewidthmm','sizewidth','widthmm','width']),0);
  const rawFormFactor=String(exactVal(m,['form_factor','formfactor','size','type'])||'').trim();
  const formFactor=normalizeMotherboardFormFactor(rawFormFactor,sourceKey,{l:rawHeight,w:rawWidth});
  const defaultRamSlots=(formFactor==='MATX'||formFactor==='ATX')?4:2;
  const ramSlots=Math.max(1,Math.round(rawRamSlots||defaultRamSlots));
  const nominal=nominalMotherboardOutline(formFactor);
  const l=rawHeight||nominal.l;
  const w=rawWidth||nominal.w;
  // h is the existing one-sided component keepout envelope, not PCB thickness.
  // The rendered PCB itself remains 1.6 mm in components/factory.js.
  const h=44;
  const baseTitle=brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
  const tags=[formFactor];
  if (socket) tags.push(socket);
  if (chipset && !tags.some(v=>String(v).toLowerCase()===chipset.toLowerCase())) tags.push(chipset);
  return {
    ...m, catalogId, brand, model, socket, chipset, formFactor, ramSlots, ramType, l, w, h,
    displayLabel:`${baseTitle} [${tags.join(' · ')}]`,
    dimensionLabel:`${l}x${w}mm`,
    dimensionKnown:{l:rawHeight>0,w:rawWidth>0,h:false},
    dimensionSource:rawHeight>0&&rawWidth>0?'catalog_board_outline':'form_factor_nominal_outline',
    sourceKey
  };
}

function normalizePsuFormFactor(raw = '') {
  const text=String(raw||'').trim().toUpperCase().replace(/[_]+/g,'-');
  if (!text) return '';
  if (text.includes('SFX-L') || text.includes('SFX L') || text.includes('SFXL')) return 'SFX-L';
  if (text.includes('FLEX')) return 'FLEX';
  if (text.includes('SFX')) return 'SFX';
  if (text.includes('ATX')) return 'ATX';
  return '';
}

function nominalPsuGeometry(formFactor = 'SFX') {
  const ff=String(formFactor||'SFX').toUpperCase();
  if (ff === 'SFX-L') return {l:125,w:63.5,h:125};
  if (ff === 'ATX') return {l:150,w:86,h:140};
  if (ff === 'FLEX') return {l:150,w:40.5,h:81.5};
  return {l:125,w:63.5,h:100};
}

export function normalizePsuRecord(p, sourceKey = '') {
  if (!p || typeof p !== 'object') return null;
  const brand=String(exactVal(p,['brand','mfr','manufacturer','vendor'])||'').trim();
  const model=String(exactVal(p,['name','model','title'])||'').trim();
  const catalogId=String(exactVal(p,['id','catalogid'])||'').trim();
  if (!model) return null;
  const rawFormFactor=String(exactVal(p,['form_factor','formfactor','psu_type','psutype','type','format'])||'').trim();
  const formFactor=normalizePsuFormFactor(rawFormFactor || `${sourceKey} ${brand} ${model}`);
  if (!formFactor) return null;
  const nominal=nominalPsuGeometry(formFactor);
  const installLength=num(exactVal(p,['psulengthmm','psulength','depthmm','depth','lengthmm','length']),0);
  const explicitWidth=num(exactVal(p,['widthmm','width']),0);
  const explicitHeight=num(exactVal(p,['heightmm','height']),0);
  let l,w,h;
  if (formFactor === 'FLEX') {
    l=installLength||nominal.l;
    w=explicitHeight||nominal.w;
    h=explicitWidth||nominal.h;
  } else {
    l=explicitWidth||nominal.l;
    w=explicitHeight||nominal.w;
    h=installLength||nominal.h;
  }
  const wattage=num(exactVal(p,['wattage','watts','ratedpower','power']),0);
  const baseTitle=brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
  const suffix=[formFactor];
  if (wattage>0) suffix.push(`${wattage}W`);
  const fullDimsKnown=explicitWidth>0&&explicitHeight>0&&installLength>0;
  const installKnown=installLength>0;
  return {
    ...p, catalogId, brand, model, formFactor, wattage, l, w, h,
    displayLabel:`${baseTitle} [${suffix.join(' · ')}]`,
    dimensionLabel:`${l}x${w}x${h}mm`,
    dimensionKnown:{l:formFactor==='FLEX'?installKnown:explicitWidth>0,w:explicitHeight>0,h:formFactor==='FLEX'?explicitWidth>0:installKnown},
    dimensionSource:fullDimsKnown?'catalog_full':installKnown?'catalog_install_length':'form_factor_nominal',
    sourceKey
  };
}

function collectHardwareRows(hw, predicate) {
  const rows=[];
  if (!hw || typeof hw !== 'object') return rows;
  for (const [key,list] of Object.entries(hw)) {
    if (!Array.isArray(list) || !predicate(String(key))) continue;
    list.forEach(item=>{ if(item && typeof item==='object') rows.push({item,sourceKey:key}); });
  }
  return rows;
}

function motherboardArrayKey(key) {
  const k=String(key||'').toLowerCase();
  if (/(case|gpu|cooler|fan|psu|power.?supply|cpu_|riser)/.test(k)) return false;
  return /(mobo|motherboard|mitx|matx|mini.?itx|micro.?atx|itx.*board|board.*itx)/.test(k);
}

function psuArrayKey(key) {
  const k=String(key||'').toLowerCase();
  return !k.includes('case') && /(psu|power.?supply)/.test(k);
}

function dedupeCatalogRecords(records, keyFn) {
  const out=[];
  const seen=new Set();
  for (const record of records) {
    if (!record) continue;
    const key=String(keyFn(record)||'').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(record);
  }
  return out;
}

function tokenList(value) {
  if (Array.isArray(value)) return value.map(v=>String(v).trim()).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'object') return Object.values(value).flatMap(tokenList);
  return String(value).split(/[,;|/]+/).map(v=>v.trim()).filter(Boolean);
}
function firstOwn(obj, keys) {
  for (const key of keys) if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  return undefined;
}
function normalizedFieldKey(value){return String(value||'').toLowerCase().replace(/[^a-z0-9]+/g,'');}
function firstOwnLoose(obj, keys){
  const exact=firstOwn(obj,keys);
  if(exact!==undefined)return exact;
  if(!obj||typeof obj!=='object')return undefined;
  const wanted=new Set(keys.map(normalizedFieldKey));
  for(const [key,value] of Object.entries(obj)){
    if(value!==undefined&&value!==null&&wanted.has(normalizedFieldKey(key)))return value;
  }
  return undefined;
}
async function fetchJsonOptional(url, options = {}) {
  try {
    const r = await fetch(url, options);
    if (!r.ok) throw new Error(`${r.status} ${url}`);
    return { ok:true, url, payload:await r.json() };
  } catch (error) {
    return { ok:false, url, error };
  }
}

async function fetchJsonCandidates(urls, options = {}) {
  let last=null;
  for (const url of urls) {
    const result=await fetchJsonOptional(url, options);
    if(result.ok)return { payload:result.payload, url:result.url };
    last=result.error;
  }
  throw last || new Error('No JSON candidate succeeded');
}

async function fetchCatalogMeta() {
  const result=await fetchJsonOptional('/meta.json', { cache:'no-store' });
  if(result.ok && result.payload && typeof result.payload==='object') {
    return { ok:true, payload:result.payload, error:null };
  }
  return { ok:false, payload:null, error:result.error || new Error('Metadata unavailable') };
}

function versionedStaticUrl(path, version) {
  const token=String(version || '').trim();
  return token ? `${path}?v=${encodeURIComponent(token)}` : path;
}

function staticFetchOptions(version) {
  return String(version || '').trim() ? { cache:'default' } : { cache:'no-cache' };
}

function normalizeRadiatorSupport(value) {
  const support={};
  const add=(rawSize, rawCount=1)=>{
    const size=Math.round(Number(rawSize));
    const count=Math.max(1,Math.round(Number(rawCount)||1));
    if(!Number.isFinite(size) || size<80 || size>1000)return;
    support[String(size)]=Math.max(support[String(size)]||0,count);
  };

  if(value && typeof value==='object' && !Array.isArray(value)) {
    for(const [size,count] of Object.entries(value))add(size,count);
    return support;
  }

  const raw=String(value || '').trim();
  if(!raw)return support;
  for(const match of raw.matchAll(/\b(\d+)\s*[x×*]\s*(\d{2,4})\b/giu))add(match[2],match[1]);
  for(const match of raw.matchAll(/\b(\d{2,4})\b/gu))add(match[1],1);
  return support;
}

function hardwareRoot(payload) {
  return payload?.hardware && typeof payload.hardware === 'object' ? payload.hardware : (payload || {});
}
function hardwareCatalogScore(payload) {
  const hw=hardwareRoot(payload);
  const keys=['gpus_lt_215','gpus_gt_215','gpus','gpu','coolers_lt_70','coolers_gt_70','coolers','cooler','aios','aio','aio_coolers','coolers_aio','liquid_coolers','aioCoolers','AIOs','radiators','ram','ram_height','memory'];
  let score=keys.reduce((total,key)=>total+(Array.isArray(hw?.[key])?hw[key].length:0),0);
  for (const [key,list] of Object.entries(hw||{})) {
    if (!Array.isArray(list)) continue;
    if (motherboardArrayKey(key) || psuArrayKey(key)) score += list.length;
  }
  return score;
}
async function fetchHardwareCatalog(meta) {
  const version=meta?.hardware_version || '';
  const url=versionedStaticUrl('/hardware_cache.json',version);
  const result=await fetchJsonOptional(url, staticFetchOptions(version));
  if(!result.ok)throw result.error || new Error(`Hardware catalog unavailable at ${url}`);
  const score=hardwareCatalogScore(result.payload);
  if(score<=0)throw new Error(`Hardware payload at ${url} contained no recognized hardware arrays`);
  return {payload:result.payload,url,score,version};
}
function firstHardwareArray(hw, keys) {
  for(const key of keys){
    if(Array.isArray(hw?.[key]) && hw[key].length)return hw[key];
  }
  return [];
}

function firstHardwareArrayLoose(hw, keys) {
  const direct=firstHardwareArray(hw,keys);
  if(direct.length)return direct;
  const wanted=new Set(keys.map(key=>String(key).toLowerCase().replace(/[^a-z0-9]/g,'')));
  for(const [key,value] of Object.entries(hw||{})){
    if(!Array.isArray(value)||!value.length)continue;
    const normalized=String(key).toLowerCase().replace(/[^a-z0-9]/g,'');
    if(wanted.has(normalized))return value;
  }
  return [];
}
function nominalRadiatorGeometry(size) {
  const n=num(size,0);
  if(n>=420)return {size:420,l:455,w:140,h:30};
  if(n>=360)return {size:360,l:397,w:120,h:30};
  if(n>=280)return {size:280,l:317,w:140,h:30};
  if(n>=240)return {size:240,l:277,w:120,h:30};
  if(n>=140)return {size:140,l:160,w:140,h:30};
  if(n>=120)return {size:120,l:157,w:120,h:30};
  return null;
}

function rawCaseList(payload) {
  return payload?.cases || (Array.isArray(payload) ? payload : []);
}

export async function loadCatalogBundle() {
  // /3d intentionally consumes the complete generated static catalogs directly.
  // meta.json is the version/sync manifest; PHP endpoints and reduced JSON variants are
  // deliberately outside this path so spatial inference always sees one physical authority.
  const metaResult=await fetchCatalogMeta();
  const meta=metaResult.payload || {};
  const caseVersion=meta?.version || '';
  const hardwareVersion=meta?.hardware_version || '';
  const structuredVersion=meta?.structured_version || '';

  const caseUrl=versionedStaticUrl('/cases_cache.json',caseVersion);
  const structuredUrls=[
    versionedStaticUrl('/cases_structured.json',structuredVersion),
    versionedStaticUrl('/database/cases_structured.json',structuredVersion)
  ];

  const [caseResult, hardwareResult, structuredResult] = await Promise.all([
    fetchJsonOptional(caseUrl, staticFetchOptions(caseVersion)),
    fetchHardwareCatalog(meta).then(value=>({status:'fulfilled',value}), reason=>({status:'rejected',reason})),
    fetchJsonCandidates(structuredUrls, staticFetchOptions(structuredVersion)).then(value=>({status:'fulfilled',value}), reason=>({status:'rejected',reason}))
  ]);

  const rawCases=caseResult.ok
    ? rawCaseList(caseResult.payload).map(c=>({...c,_spatial_catalog_source:'cases_cache'}))
    : [];
  const cases = rawCases.filter(c=>c&&c.name).map(c=>{
    const rawDims = c.caseDimensions || c.dims || '';
    const dims = Array.isArray(rawDims) ? rawDims.map(Number) : String(rawDims).replace(/ mm/g,'').split(/\s*x\s*/i).map(Number);
    const layout=findVal(c,['style','style/layout','layout'])||'unknown';
    const rawRadiatorSupport=firstOwnLoose(c,['radiatorSupport','radiator support','aio / radiator support','aioRadiatorSupport']);
    const normalized={
      ...c,
      raw:c,
      name:c.name,
      catalogId:String(c.id || c.catalogId || '').trim(),
      caseKey:c._case_key || c.case_key || c.case_id || '',
      volume:Number(c.volume ?? c._volume) || 0,
      l:Number(dims[0])||0,w:Number(dims[1])||0,h:Number(dims[2])||0,
      isCase:true,
      style:layout,
      maxCPU:num(c.maxCPU ?? c._maxCPU,0),
      maxGPU:num(c.maxGPU ?? c._maxGPU,0),
      maxThick:num(c.maxThick ?? c._maxThick,0),
      maxGPUHeight:num(c.maxGPUHeight ?? c.maxGPUH,0),
      maxAIO:num(c.maxAIO ?? c._maxAIO ?? c.maxRadiator,0),
      maxPSU:num(c.maxPSU ?? c._maxPSU,0),
      supportedMobo: tokenList(firstOwnLoose(c,['supportedMobo','moboSupport','motherboardSupport','motherboardCompatibility','motherboard support','motherboard compatibility','mobo','motherboard'])),
      supportedPSU: tokenList(firstOwnLoose(c,['supportedPSU','supportedPsu','psuSupport','powerSupplySupport','powerSupplyCompatibility','power supply support','power supply compatibility','psu','powerSupply'])),
      radiatorSupport:normalizeRadiatorSupport(rawRadiatorSupport),
      declaredLayout:layout
    };
    normalized.physicalLayout=classifyPhysicalLayout(normalized,{family:layout});
    return normalized;
  }).filter(c=>c.l>0&&c.w>0&&c.h>0);

  const hwPayload = hardwareResult.status==='fulfilled' ? hardwareResult.value.payload : {};
  const hw = hardwareRoot(hwPayload);
  const rawGpuRows=[...(hw.gpus_lt_215||[]),...(hw.gpus_gt_215||[]),...(hw.gpus||hw.gpu||[])];
  const normalizedGpuRows=rawGpuRows.map(normalizeGpuRecord);
  const gpus=normalizedGpuRows.filter(g=>g.l>0&&g.w>0&&g.h>0);
  const coolers=[...(hw.coolers_lt_70||[]),...(hw.coolers_gt_70||[]),...(hw.coolers||hw.cooler||[])]
    .map(normalizeCoolerRecord).filter(c=>c.l>0&&c.w>0&&c.h>0);
  const rawRam=firstHardwareArrayLoose(hw,['ram','ram_height','ramheight','memory','rams','RAM Height','RAM']);
  const rams=rawRam.map(normalizeRamRecord).filter(r=>r.h>0);
  const rawAios=firstHardwareArray(hw,['aios','aio','aio_coolers','coolers_aio','liquid_coolers','aioCoolers','AIOs','radiators']);
  const aios=rawAios.map(normalizeAioRecord).filter(a=>a.l>0&&a.w>0&&a.h>0);

  const rawMotherboards=collectHardwareRows(hw,motherboardArrayKey);
  const motherboards=dedupeCatalogRecords(
    rawMotherboards.map(({item,sourceKey})=>normalizeMotherboardRecord(item,sourceKey)).filter(Boolean),
    row=>`${row.brand}|${row.model}|${row.formFactor}`
  );
  const rawPsus=collectHardwareRows(hw,psuArrayKey);
  const psus=dedupeCatalogRecords(
    rawPsus.map(({item,sourceKey})=>normalizePsuRecord(item,sourceKey)).filter(Boolean),
    row=>`${row.brand}|${row.model}|${row.formFactor}`
  );

  const structuredRaw = structuredResult.status==='fulfilled' ? structuredResult.value.payload : [];
  const radiatorCapabilityRecords=cases.filter(c=>Object.keys(c.radiatorSupport||{}).length>0).length;
  const multiRadiatorCapabilityRecords=cases.filter(c=>Object.values(c.radiatorSupport||{}).some(count=>Number(count)>1)).length;
  const spatialEvidenceRecords=cases.filter(c=>c?.spatialHints&&typeof c.spatialHints==='object').length;
  const spatialFanEvidenceRecords=cases.filter(c=>Array.isArray(c?.spatialHints?.fans)&&c.spatialHints.fans.length>0).length;
  const spatialRadiatorEvidenceRecords=cases.filter(c=>Array.isArray(c?.spatialHints?.radiators)&&c.spatialHints.radiators.length>0).length;

  return {
    cases, gpus, coolers, rams, aios, motherboards, psus,
    meta:metaResult.ok ? meta : null,
    structuredIndex:buildStructuredOverlayIndex(structuredRaw),
    diagnostics:{
      meta:metaResult.ok?'fulfilled':'rejected',
      metaVersion:caseVersion || null,
      hardwareVersion:hardwareVersion || null,
      structuredVersion:structuredVersion || null,
      caseSource:caseResult.ok?'/cases_cache.json':null,
      casesCache:caseResult.ok,
      caseRequestUrl:caseUrl,
      hardware:hardwareResult.status,
      hardwareSource:hardwareResult.status==='fulfilled'?'/hardware_cache.json':null,
      hardwareRequestUrl:hardwareResult.status==='fulfilled'?hardwareResult.value.url:versionedStaticUrl('/hardware_cache.json',hardwareVersion),
      hardwareRecognizedRecords:hardwareResult.status==='fulfilled'?hardwareResult.value.score:0,
      radiatorCapabilityRecords,
      multiRadiatorCapabilityRecords,
      spatialEvidenceRecords,
      spatialFanEvidenceRecords,
      spatialRadiatorEvidenceRecords,
      gpuRawRecords:rawGpuRows.length,
      gpuNormalizedRecords:gpus.length,
      ramRawRecords:rawRam.length,
      ramNormalizedRecords:rams.length,
      catalogIdSchema:meta?.catalog_ids?.schema || null,
      caseCatalogIds:cases.filter(c=>c.catalogId).length,
      hardwareCatalogIds:[...gpus,...coolers,...rams,...aios,...motherboards,...psus].filter(r=>r.catalogId).length,
      gpuMissingDimensionRecords:normalizedGpuRows.length-gpus.length,
      aioRawRecords:rawAios.length, aioNormalizedRecords:aios.length,
      aioCatalogDimensionRecords:aios.filter(a=>a.dimensionSource==='catalog_radiator_dimensions').length,
      aioFallbackDimensionRecords:aios.filter(a=>a.dimensionSource!=='catalog_radiator_dimensions').length,
      aioKnownFanThicknessRecords:aios.filter(a=>a.fanThicknessKnown).length,
      aioCpuBlockRecords:aios.filter(a=>a.aioBlock?.h>0).length,
      aioTubeLengthRecords:aios.filter(a=>a.tubeLengthKnown).length,
      motherboardRawRecords:rawMotherboards.length, motherboardNormalizedRecords:motherboards.length,
      motherboardCatalogOutlineRecords:motherboards.filter(m=>m.dimensionSource==='catalog_board_outline').length,
      psuRawRecords:rawPsus.length, psuNormalizedRecords:psus.length,
      psuCatalogInstallLengthRecords:psus.filter(p=>p.dimensionSource!=='form_factor_nominal').length,
      structured:structuredResult.status,
      structuredSource:structuredResult.status==='fulfilled'?structuredResult.value.url:null
    }
  };
}
